/**
 * G2eGente - Visualização/Edição de Avaliação de Desempenho
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { performanceReviewsAPI, performanceCyclesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Loader2,
  User,
  Calendar,
  Star,
  Edit,
  Trash2,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const CLASSIFICACAO_COLORS = {
  excepcional: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  supera: 'bg-green-100 text-green-700 border-green-200',
  atende: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  abaixo: 'bg-red-100 text-red-700 border-red-200'
};

const CLASSIFICACAO_LABELS = {
  excepcional: 'Excepcional',
  supera: 'Supera Expectativas',
  atende: 'Atende Expectativas',
  abaixo: 'Abaixo do Esperado'
};

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-amber-100 text-amber-700', icon: Clock },
  finalizado: { label: 'Finalizado', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

export default function PerformanceReviewDetail() {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Form data for editing
  const [formData, setFormData] = useState({
    competencias_tecnicas: [],
    competencias_comportamentais: [],
    disc_observacoes: '',
    pontos_fortes: '',
    pontos_melhoria: '',
    feedback_gestor: ''
  });

  useEffect(() => {
    fetchReview();
  }, [reviewId]);

  const fetchReview = async () => {
    try {
      const res = await performanceReviewsAPI.getById(reviewId);
      setReview(res.data);
      
      // Preparar dados para edição
      setFormData({
        competencias_tecnicas: res.data.competencias_tecnicas || [],
        competencias_comportamentais: res.data.competencias_comportamentais || [],
        disc_observacoes: res.data.disc_observacoes || '',
        pontos_fortes: res.data.pontos_fortes || '',
        pontos_melhoria: res.data.pontos_melhoria || '',
        feedback_gestor: res.data.feedback_gestor || ''
      });
    } catch (error) {
      toast.error('Erro ao carregar avaliação');
      navigate('/company/performance');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await performanceReviewsAPI.update(reviewId, formData);
      toast.success('Avaliação atualizada com sucesso!');
      setEditing(false);
      fetchReview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await performanceReviewsAPI.delete(reviewId);
      toast.success('Avaliação excluída com sucesso!');
      navigate('/company/performance');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setDeleting(false);
      setDeleteModal(false);
    }
  };

  const updateCompetenciaNota = (tipo, index, nota) => {
    const key = tipo === 'tecnica' ? 'competencias_tecnicas' : 'competencias_comportamentais';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].map((c, i) => i === index ? { ...c, nota: parseInt(nota) } : c)
    }));
  };

  const updateCompetenciaObs = (tipo, index, observacao) => {
    const key = tipo === 'tecnica' ? 'competencias_tecnicas' : 'competencias_comportamentais';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].map((c, i) => i === index ? { ...c, observacao } : c)
    }));
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!review) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="text-center py-12">
          <p className="text-slate-500">Avaliação não encontrada</p>
          <Button onClick={() => navigate('/company/performance')} className="mt-4">
            Voltar
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const StatusIcon = STATUS_CONFIG[review.status]?.icon || Clock;
  const canEdit = review.status === 'rascunho';

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="performance-review-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/company/performance')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Avaliação de Desempenho</h1>
              <p className="text-slate-600">{review.employee_nome} • {review.cycle_nome}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canEdit && !editing && (
              <Button 
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            )}
            {editing && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    fetchReview(); // Reset form
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salvar
                </Button>
              </>
            )}
            <Button 
              variant="destructive"
              onClick={() => setDeleteModal(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Funcionário</p>
                  <p className="font-semibold text-slate-900">{review.employee_nome}</p>
                  <p className="text-xs text-slate-500">{review.employee_cargo}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Ciclo</p>
                  <p className="font-semibold text-slate-900">{review.cycle_nome}</p>
                  <p className="text-xs text-slate-500">{review.cycle_periodo}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Média Geral</p>
                  <p className="font-bold text-2xl text-slate-900">
                    {review.media_geral?.toFixed(2) || '-'}
                  </p>
                  <p className="text-xs text-slate-500">/5.00</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border ${review.classificacao ? CLASSIFICACAO_COLORS[review.classificacao] : 'border-slate-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${STATUS_CONFIG[review.status]?.color || 'bg-slate-100'}`}>
                  <StatusIcon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status / Classificação</p>
                  <Badge className={STATUS_CONFIG[review.status]?.color}>
                    {STATUS_CONFIG[review.status]?.label || review.status}
                  </Badge>
                  {review.classificacao && (
                    <p className="text-xs mt-1 font-medium">
                      {CLASSIFICACAO_LABELS[review.classificacao]}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Competências Técnicas */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Competências Técnicas</CardTitle>
          </CardHeader>
          <CardContent>
            {(editing ? formData.competencias_tecnicas : review.competencias_tecnicas)?.length === 0 ? (
              <p className="text-slate-500 text-sm">Nenhuma competência técnica avaliada</p>
            ) : (
              <div className="space-y-4">
                {(editing ? formData.competencias_tecnicas : review.competencias_tecnicas)?.map((comp, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{comp.nome}</h4>
                      {editing ? (
                        <Select 
                          value={String(comp.nota || '')} 
                          onValueChange={(v) => updateCompetenciaNota('tecnica', idx, v)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Nota" />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star 
                              key={n} 
                              className={`w-5 h-5 ${n <= (comp.nota || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} 
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {editing ? (
                      <Textarea
                        value={comp.observacao || ''}
                        onChange={(e) => updateCompetenciaObs('tecnica', idx, e.target.value)}
                        placeholder="Observações sobre esta competência..."
                        rows={2}
                      />
                    ) : comp.observacao && (
                      <p className="text-sm text-slate-600">{comp.observacao}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Competências Comportamentais */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Competências Comportamentais</CardTitle>
          </CardHeader>
          <CardContent>
            {(editing ? formData.competencias_comportamentais : review.competencias_comportamentais)?.length === 0 ? (
              <p className="text-slate-500 text-sm">Nenhuma competência comportamental avaliada</p>
            ) : (
              <div className="space-y-4">
                {(editing ? formData.competencias_comportamentais : review.competencias_comportamentais)?.map((comp, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{comp.nome}</h4>
                      {editing ? (
                        <Select 
                          value={String(comp.nota || '')} 
                          onValueChange={(v) => updateCompetenciaNota('comportamental', idx, v)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Nota" />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star 
                              key={n} 
                              className={`w-5 h-5 ${n <= (comp.nota || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} 
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {editing ? (
                      <Textarea
                        value={comp.observacao || ''}
                        onChange={(e) => updateCompetenciaObs('comportamental', idx, e.target.value)}
                        placeholder="Observações sobre esta competência..."
                        rows={2}
                      />
                    ) : comp.observacao && (
                      <p className="text-sm text-slate-600">{comp.observacao}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback e Observações */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Pontos Fortes</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Textarea
                  value={formData.pontos_fortes}
                  onChange={(e) => setFormData({...formData, pontos_fortes: e.target.value})}
                  placeholder="Descreva os pontos fortes do funcionário..."
                  rows={4}
                />
              ) : (
                <p className="text-slate-700 whitespace-pre-wrap">
                  {review.pontos_fortes || 'Não informado'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Pontos de Melhoria</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Textarea
                  value={formData.pontos_melhoria}
                  onChange={(e) => setFormData({...formData, pontos_melhoria: e.target.value})}
                  placeholder="Descreva os pontos de melhoria..."
                  rows={4}
                />
              ) : (
                <p className="text-slate-700 whitespace-pre-wrap">
                  {review.pontos_melhoria || 'Não informado'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Feedback do Gestor</CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Textarea
                value={formData.feedback_gestor}
                onChange={(e) => setFormData({...formData, feedback_gestor: e.target.value})}
                placeholder="Feedback geral do gestor para o funcionário..."
                rows={4}
              />
            ) : (
              <p className="text-slate-700 whitespace-pre-wrap">
                {review.feedback_gestor || 'Nenhum feedback registrado'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Delete Modal */}
        <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Excluir Avaliação
              </DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir esta avaliação de desempenho de {review.employee_nome}? 
                Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal(false)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
